import { useEffect } from "react";
import { useI18n } from "@/lib/i18n";

/**
 * Sets document.title, description meta tag, JSON-LD, and hreflang alternates.
 * Alternates are computed automatically from the current URL using the
 * i18n language context — /services/x pairs with /ar/services/x etc.
 */
export function Seo({ title, description, jsonLd, hreflangEn, hreflangAr }) {
  const { lang, arPath, enPath } = useI18n();

  useEffect(() => {
    const brand = lang === "ar" ? "وفا سوليوشنز" : "Wafa Solutions LLC";
    const fullTitle = title ? `${title} · ${brand}` : brand;
    const prevTitle = document.title;
    document.title = fullTitle;

    let metaEl = document.querySelector('meta[name="description"]');
    const prevDesc = metaEl ? metaEl.getAttribute("content") : null;
    if (!metaEl && description) {
      metaEl = document.createElement("meta");
      metaEl.setAttribute("name", "description");
      document.head.appendChild(metaEl);
    }
    if (metaEl && description != null) metaEl.setAttribute("content", description);

    // hreflang alternates. Written to page as <link rel="alternate">. The
    // href is a full URL when window.location.origin is known so crawlers
    // parse it correctly. When a page's Arabic slug differs from its
    // English slug (Arabic-friendly SEO URLs), pass explicit hreflangEn
    // and hreflangAr so the pair still points at the correct translation.
    const origin = (typeof window !== "undefined" && window.location.origin) || "";
    const enHref = hreflangEn || enPath;
    const arHref = hreflangAr || arPath;
    const linkEls = [];
    const alt = (hreflang, path) => {
      const el = document.createElement("link");
      el.setAttribute("rel", "alternate");
      el.setAttribute("hreflang", hreflang);
      el.setAttribute("href", origin + path);
      el.dataset.seoAlt = "1";
      document.head.appendChild(el);
      linkEls.push(el);
    };
    alt("en", enHref);
    alt("ar", arHref);
    alt("x-default", enHref);

    const scriptEls = [];
    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    blocks.forEach((data, i) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.seoBlock = `wafa-${i}`;
      el.text = JSON.stringify(data);
      document.head.appendChild(el);
      scriptEls.push(el);
    });

    return () => {
      document.title = prevTitle;
      if (metaEl && prevDesc != null) metaEl.setAttribute("content", prevDesc);
      linkEls.forEach((el) => el.remove());
      scriptEls.forEach((el) => el.remove());
    };
  }, [title, description, JSON.stringify(jsonLd || null), lang, arPath, enPath, hreflangEn, hreflangAr]);

  return null;
}
